<template>
  <div class="orders-refunded-shopify-page">
    <BaseTable
      :loading="loading"
      :items="items"
      :columns="columns"
      :current-page="currentPage"
      :total-pages="totalPages"
      :total-items="totalItems"
      :items-per-page="itemsPerPage"
      :show-pagination="true"
      :show-search="true"
      :search-placeholder="$t('refunds.search_placeholder')"
      :show-tabs-wrapper="true"
      @page-change="handlePageChange"
      @items-per-page-change="handleItemsPerPageChange"
      @search="handleSearch"
    >
      <template #header-text>
        <h1 class="page-title">{{ $t("refunds.shopify.description") }}</h1>
      </template>

      <template #filters>
        <FilterSidebarTable
          @filters-change="handleFiltersChange"
          :initial-filters="activeFilters"
          :table-filters="tableFilters"
        >
          <template
            #filters="{
              filters,
              updateFilter,
              operators,
              tableFilters,
              dateFilters,
              updateDateFilter,
            }"
          >
            <!-- Filtre de marques -->
            <BrandFilter
              :value="filters.selectedBrands"
              :operator="filters.brandOperator"
              :operators="operators"
              :table-filters="tableFilters"
              @update="updateFilter"
            />

            <!-- Filtre de date de création -->
            <DateFilter
              type="stockDate"
              :label="$t('refunds.columns.created_at')"
              :value1="dateFilters.stockDate.value1"
              :value2="dateFilters.stockDate.value2"
              @update="updateDateFilter"
            />
          </template>
        </FilterSidebarTable>

        <ButtonExport @export="handleExport" :loading="exportLoading" />
      </template>

      <template #cell-shopifyOrderId="{ item }">
        <div class="reference-container">
          <span class="reference-text">{{ item.shopifyOrder.shopifyId }}</span>
          <button
            class="copy-button"
            @click.stop="copyToClipboard(item.shopifyOrder.shopifyId)"
          >
            <SvgIcon name="copy" />
          </button>
        </div>
      </template>

      <template #cell-brand="{ item }">
        {{ item.shopifyOrder.client.name }}
      </template>

      <template #cell-createdAt="{ item }">
        {{ formatDate(item.createdAt) }}
      </template>

      <template #cell-mode="{ item }">
        <span class="badge" :class="`badge--${item.mode}`">
          {{ $t(`refunds.mode.${item.mode}`) }}
        </span>
      </template>

      <template #cell-cardRefundedAmount="{ item }">
        {{ formatAmount(item.cardRefundedAmount, item.currency) }}
      </template>

      <template #cell-walletRefundedAmount="{ item }">
        {{ formatAmount(item.walletRefundedAmount, item.currency) }}
      </template>

      <template #cell-amountWithTax="{ item }">
        {{ formatAmount(item.amountWithTax, item.currency) }}
      </template>

      <template #cell-methods="{ item }">
        <div class="methods-list">
          <span
            v-for="(method, index) in item.methods"
            :key="index"
            class="method-badge"
          >
            {{ $t(`refunds.methods.${method}`) }}
          </span>
        </div>
      </template>

      <template #cell-reason="{ item }">
        <span class="reason-text">
          {{ $t(`refunds.reasons.${item.reason}`) }}
        </span>
      </template>
    </BaseTable>
  </div>
</template>

<script>
import { mapState } from "vuex";
import BaseTable from "@/components/ui/dataTable/BaseTable.vue";
import SvgIcon from "@/components/ui/svg/SvgIcon.vue";
import FilterSidebarTable from "@/components/ui/dataTable/actions-table/FilterSidebarTable.vue";
import BrandFilter from "@/components/ui/dataTable/filters/BrandFilter.vue";
import DateFilter from "@/components/ui/dataTable/filters/DateFilter.vue";
import ButtonExport from "@/components/ui/button/ButtonExport.vue";
import dateFormatMixin from "@/mixins/dateFormatMixin";

export default {
  name: "OrdersRefundedShopifyPage",
  components: {
    BaseTable,
    SvgIcon,
    FilterSidebarTable,
    BrandFilter,
    DateFilter,
    ButtonExport,
  },
  mixins: [dateFormatMixin],

  data() {
    return {
      loading: true,
      exportLoading: false,
      currentPage: 1,
      itemsPerPage: 10,
      searchQuery: "",
      activeFilters: {
        brands: {
          values: [],
          operator: "inclus",
        },
        stockDate: {
          value1: "",
          value2: "",
          operator: "",
        },
      },
    };
  },

  computed: {
    ...mapState({
      tableData: (state) => state.ordersRefundedShopify,
      tableFilters: (state) => state.ordersRefundedShopifyFilters,
    }),

    items() {
      return this.tableData?.items || [];
    },

    totalPages() {
      return this.tableData?.pagination?.pageCount || 1;
    },

    totalItems() {
      return this.tableData?.pagination?.totalCount || 0;
    },

    columns() {
      return [
        { key: "id", label: this.$t("refunds.columns.id") },
        {
          key: "shopifyOrderId",
          label: this.$t("refunds.columns.shopify_order_id"),
        },
        { key: "brand", label: this.$t("refunds.columns.brand") },
        { key: "createdAt", label: this.$t("refunds.columns.created_at") },
        { key: "mode", label: this.$t("refunds.columns.mode") },
        {
          key: "cardRefundedAmount",
          label: this.$t("refunds.columns.card_refunded_amount"),
        },
        {
          key: "walletRefundedAmount",
          label: this.$t("refunds.columns.wallet_refunded_amount"),
        },
        { key: "amountWithTax", label: this.$t("refunds.columns.amount") },
        { key: "methods", label: this.$t("refunds.columns.methods") },
        { key: "reason", label: this.$t("refunds.columns.reason") },
      ];
    },

    queryString() {
      const params = new URLSearchParams();
      params.set("page", this.currentPage);
      params.set("limit", this.itemsPerPage);

      if (this.searchQuery) {
        params.set("search", this.searchQuery);
      }

      // Filtres de marque
      if (
        this.activeFilters.brands &&
        this.activeFilters.brands.values.length > 0
      ) {
        params.set("brand[in]", this.activeFilters.brands.values.join(","));
      }

      // Filtre de date de création (mappé depuis stockDate)
      if (this.activeFilters.stockDate) {
        if (this.activeFilters.stockDate.value1) {
          params.set(
            "createdAt[gte]",
            `${this.activeFilters.stockDate.value1} 00:00:00`
          );
        }
        if (this.activeFilters.stockDate.value2) {
          params.set(
            "createdAt[lte]",
            `${this.activeFilters.stockDate.value2} 23:59:59`
          );
        }
      }

      return params.toString();
    },
  },

  methods: {
    async fetchData() {
      this.loading = true;
      try {
        await this.$store.dispatch("getTable", {
          name: "ordersRefundedShopify",
          url: this.$listRoutes.ordersRefundedShopify,
          filters: this.queryString,
        });
      } catch (error) {
        console.error("Error fetching refunds:", error);
      } finally {
        this.loading = false;
      }
    },

    async fetchFilters() {
      await this.$store.dispatch("getTableFilters", {
        name: "ordersRefundedShopify",
        url: this.$listRoutes.ordersRefundedShopify,
      });
    },

    handlePageChange(page) {
      this.currentPage = page;
      this.fetchData();
    },

    handleItemsPerPageChange(value) {
      this.itemsPerPage = value;
      this.currentPage = 1;
      this.fetchData();
    },

    handleSearch(value) {
      this.searchQuery = value;
      this.currentPage = 1;
      this.fetchData();
    },

    handleFiltersChange(filters) {
      // Mettre à jour les filtres actifs
      this.activeFilters = {
        brands: {
          values: filters.brands?.values || [],
          operator: filters.brands?.operator || "inclus",
        },
        stockDate: {
          value1: filters.stockDate?.value1 || "",
          value2: filters.stockDate?.value2 || "",
          operator: filters.stockDate?.operator || "",
        },
      };

      // Réinitialiser à la première page et récupérer les données
      this.currentPage = 1;
      this.fetchData();
    },

    async handleExport(format) {
      this.exportLoading = true;
      try {
        await this.$store.dispatch("exportTable", {
          name: "ordersRefundedShopify",
          url: this.$listRoutes.ordersRefundedShopify,
          filters: this.queryString,
          format,
        });
      } catch (error) {
        if (typeof error === "object" && error?.status === 202) {
          this.$store.commit("SET_NOTIFICATION_MESSAGE", {
            text: this.$t("global.export_table_by_email"),
            color: "green",
            confirmation: false,
            duration: 5000,
          });
        } else {
          console.error("Export error:", error);
        }
      } finally {
        this.exportLoading = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return "-";
      return this.formatDateForDisplay(dateString.split("T")[0]);
    },

    formatAmount(amount, currency) {
      if (amount == null) return "-";
      // Amount is in cents
      const value = amount / 100;
      return new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: currency || "EUR",
      }).format(value);
    },

    copyToClipboard(text) {
      navigator.clipboard.writeText(text);
      this.$store.commit("SET_NOTIFICATION_MESSAGE", {
        text: this.$t("global.copied"),
        color: "green",
        confirmation: false,
        duration: 3000,
      });
    },
  },

  created() {
    this.fetchData();
    this.fetchFilters();
  },
};
</script>

<style scoped>
.page-title {
  font-size: 24px;
  font-weight: bold;
  margin: 0;
  padding: 0;
}

/* Agrandir la barre de recherche principale uniquement */
.orders-refunded-shopify-page :deep(.actions-wrapper .toolbar .search-container .search-input),
.orders-refunded-shopify-page
  :deep(.actions-wrapper .toolbar .search-containe .search-container input) {
  min-width: 400px;
}

/* Adapter les colonnes à leur contenu sans ellipses */
.orders-refunded-shopify-page :deep(th),
.orders-refunded-shopify-page :deep(td) {
  max-width: none !important;
  min-width: auto !important;
  overflow: visible !important;
  text-overflow: clip !important;
  white-space: nowrap !important;
}

.btn-export {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #000;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.2s;
}

.btn-export:hover:not(:disabled) {
  background: #333;
}

.btn-export:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.loader-icon {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.opacity-50 {
  opacity: 0.5;
}

.reference-container {
  display: flex;
  align-items: center;
  gap: 8px;
}

.copy-button {
  padding: 4px;
  background: none;
  border: none;
  cursor: pointer;
  border-radius: 4px;
  transition: background 0.2s;
}

.copy-button:hover {
  background: #f0f0f0;
}

.badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.badge--auto {
  background: #e3f2fd;
  color: #1976d2;
  border: 1px solid #1976d2;
  border-radius: 999px;
}

.badge--manual {
  background: #fff3e0;
  color: #f57c00;
  border: 1px solid #f57c00;
  border-radius: 999px;
}

.methods-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.method-badge {
  display: inline-block;
  padding: 2px 6px;
  background: #f5f5f5;
  border-radius: 3px;
  font-size: 11px;
  color: #666;
  text-align: center;
}

.reason-text {
  font-size: 12px;
  color: #666;
}
</style>
